import * as React from 'react';
import { useState, useEffect } from 'react';
import _ from 'underscore';

import { hashToCollection, SortableList, TextEditor } from '../../index';

export interface FocusPoint {
  conceptResults?: { [key: string]: { name: string; correct: boolean } };
  feedback: string;
  key: string;
  name?: string;
  order: number;
  text: string;
}

export interface FocusPointsContainerProps {
  baseUrl: string;
  focusPoints: { [key: string]: FocusPoint };
  onDeleteFocusPoint: (questionID: string, focusPointID: string) => void;
  onSubmitEditedFocusPoint: (questionID: string, data: FocusPoint, key: string) => void;
  onUpdateFocusPoints: (questionID: string, focusPoints: { [key: string]: FocusPoint }, callback?: () => void) => void;
  questionID: string;
}

const FocusPointsContainer: React.FC<FocusPointsContainerProps> = ({
  baseUrl,
  focusPoints: initialFocusPoints,
  onDeleteFocusPoint,
  onSubmitEditedFocusPoint,
  onUpdateFocusPoints,
  questionID
}) => {
  const focusPointsCollection = hashToCollection(initialFocusPoints);
  const initialOrderedIds = focusPointsCollection.sort((a, b) => a.order - b.order).map((fp) => fp.key);

  const [orderedIds, setOrderedIds] = useState<string[]>(initialOrderedIds);
  const [focusPoints, setFocusPoints] = useState(initialFocusPoints);

  // Update state when props change
  useEffect(() => {
    const incomingOrderedIds = hashToCollection(initialFocusPoints)
      .sort((a, b) => a.order - b.order)
      .map((fp) => fp.key);

    if (!_.isEqual(focusPoints, initialFocusPoints)) {
      setFocusPoints(initialFocusPoints);
      setOrderedIds(incomingOrderedIds);
    }
  }, [initialFocusPoints]);

  const alertSaved = () => {
    alert('Saved!');
  };

  const alertDeleted = () => {
    alert('Deleted!');
  };

  const deleteFocusPoint = (focusPointID: string) => {
    if (confirm('⚠️ Are you sure you want to delete this? 😱')) {
      onDeleteFocusPoint(questionID, focusPointID);

      const updatedFocusPoints = { ...focusPoints };
      delete updatedFocusPoints[focusPointID];

      const newOrderedIds = orderedIds.filter((id) => id !== focusPointID);
      const focusPointsArray = hashToCollection(updatedFocusPoints);

      const newFocusPoints: { [key: string]: FocusPoint } = {};
      newOrderedIds.forEach((id, index) => {
        const focusPoint = focusPointsArray.find((fp) => fp.key === id);
        if (focusPoint) {
          focusPoint.order = index;
          newFocusPoints[focusPoint.key] = focusPoint;
        }
      });

      if (focusPointsArray.length > 0) {
        onUpdateFocusPoints(questionID, newFocusPoints, alertDeleted);
      } else {
        alertDeleted();
      }

      setOrderedIds(newOrderedIds);
      setFocusPoints(newFocusPoints);
    }
  };

  const deleteConceptResult = (conceptResultKey: string, focusPointKey: string) => {
    if (confirm('⚠️ Are you sure you want to delete this? 😱')) {
      const data = { ...focusPoints[focusPointKey] };
      if (data.conceptResults) {
        delete data.conceptResults[conceptResultKey];
      }
      onSubmitEditedFocusPoint(questionID, data, focusPointKey);
    }
  };

  const handleDeleteConceptResult = (key: string, focusPointKey: string) => {
    deleteConceptResult(key, focusPointKey);
  };

  const handleDeleteFocusPoint = (key: string) => {
    deleteFocusPoint(key);
  };

  const removeEmptyFocusPoints = (focusPoints: { [key: string]: FocusPoint }) => {
    return _.mapObject(focusPoints, (val) =>
      Object.assign({}, val, {
        text: val.text
          .split(/\|{3}(?!\|)/)
          .filter((val) => val !== '')
          .join('|||'),
      })
    );
  };

  const addNewFocusPoint = (e: React.MouseEvent, key: string) => {
    const className = `regex-${key}`;
    const value = `${Array.from(document.getElementsByClassName(className))
      .map((i: any) => i.value)
      .filter((val) => val !== '')
      .join('|||')}|||`;

    const updatedFocusPoints = { ...focusPoints };
    updatedFocusPoints[key].text = value;
    setFocusPoints(updatedFocusPoints);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    const updatedFocusPoints = { ...focusPoints };
    updatedFocusPoints[key].name = e.target.value;
    setFocusPoints(updatedFocusPoints);
  };

  const handleFocusPointChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    const className = `regex-${key}`;
    const value = `${Array.from(document.getElementsByClassName(className))
      .map((i: any) => i.value)
      .filter((val) => val !== '')
      .join('|||')}`;

    if (value === '') {
      if (!confirm('Deleting this regex will delete the whole focus point. Are you sure you want that?')) {
        return;
      }
    }

    const updatedFocusPoints = { ...focusPoints };
    updatedFocusPoints[key].text = value;
    setFocusPoints(updatedFocusPoints);
  };

  const handleFeedbackChange = (feedback: string, key: string) => {
    const updatedFocusPoints = { ...focusPoints };
    updatedFocusPoints[key].feedback = feedback;
    setFocusPoints(updatedFocusPoints);
  };

  const inputElement = (className: string, text: string, key: string) => {
    return (
      <input
        aria-label="Regex pattern"
        className={className}
        onChange={(e) => handleFocusPointChange(e, key)}
        style={{ marginBottom: 5, minWidth: `${(text.length + 1) * 8}px` }}
        type="text"
        value={text || ''}
      />
    );
  };

  const focusPointsSortedByOrder = () => {
    const focusPointsCollection = hashToCollection(focusPoints);
    return orderedIds.map((id) => focusPointsCollection.find((fp) => fp.key === id)).filter(Boolean) as FocusPoint[];
  };

  const sortCallback = (sortInfo: any[]) => {
    const newOrderedIds = sortInfo.map((item) => item.key);
    setOrderedIds(newOrderedIds);
  };

  const handleSaveAllFocusPoints = () => {
    const newFocusPoints: { [key: string]: FocusPoint } = {};
    const focusPointsArray = hashToCollection(focusPoints);

    orderedIds.forEach((id, index) => {
      const focusPoint = focusPointsArray.find((fp) => fp.key === id);
      if (focusPoint) {
        focusPoint.order = index;
        newFocusPoints[focusPoint.key] = focusPoint;
      }
    });

    onUpdateFocusPoints(questionID, newFocusPoints, alertSaved);
  };

  const renderConceptResults = (concepts: { [key: string]: { name: string; correct: boolean } } | undefined, focusPointKey: string) => {
    if (concepts) {
      const components = _.mapObject(concepts, (val, key) => (
        <p className="control sub-title is-6" key={`${val.name}`}>
          {val.name}
          {val.correct ? (
            <span className="tag is-small is-success" style={{ marginLeft: 5 }}>
              Correct
            </span>
          ) : (
            <span className="tag is-small is-danger" style={{ marginLeft: 5 }}>
              Incorrect
            </span>
          )}
          <button
            className="tag is-small is-warning"
            onClick={() => handleDeleteConceptResult(key, focusPointKey)}
            style={{ cursor: 'pointer', marginLeft: 5 }}
            type="button"
          >
            Delete
          </button>
        </p>
      ));
      return _.values(components);
    }
  };

  const renderTextInputFields = (focusPointString: string, key: string) => {
    const className = `input regex-inline-edit regex-${key}`;
    if (focusPointString === '') return inputElement(className, '', key);
    return focusPointString.split(/\|{3}(?!\|)/).map((text, index) => (
      <React.Fragment key={`${key}-${index}`}>
        {inputElement(className, text, key)}
      </React.Fragment>
    ));
  };

  const renderSaveButton = () => {
    return (
      <button
        className="button is-outlined is-primary"
        onClick={handleSaveAllFocusPoints}
        style={{ float: 'right' }}
        type="button"
      >
        Save Focus Points
      </button>
    );
  };

  const renderFocusPointsList = () => {
    const components = focusPointsSortedByOrder().map((fp) => {
      const onClickDelete = () => {
        handleDeleteFocusPoint(fp.key);
      };

      return (
        <div className="card is-fullwidth has-bottom-margin" id={fp.key} key={fp.key}>
          <header className="card-header">
            <input
              aria-label="Focus point name"
              className="regex-name"
              onChange={(e) => handleNameChange(e, fp.key)}
              placeholder="Name"
              type="text"
              value={fp.name || ''}
            />
          </header>
          <header className="card-header">
            <p className="card-header-title" style={{ display: 'inline-block' }}>
              {renderTextInputFields(fp.text, fp.key)}
              <button className="add-regex-button" onClick={(e) => addNewFocusPoint(e, fp.key)} type="button">
                +
              </button>
            </p>
            <p className="card-header-icon">{fp.order}</p>
          </header>
          <div className="card-content">
            <label className="label" htmlFor="feedback" style={{ marginTop: 10 }}>
              Feedback
            </label>
            <TextEditor handleTextChange={(e) => handleFeedbackChange(e, fp.key)} key="feedback" text={fp.feedback} />
            <br />
            {renderConceptResults(fp.conceptResults, fp.key)}
          </div>
          <footer className="card-footer">
            <a className="card-footer-item" href={`${baseUrl}/${fp.key}/edit`}>
              Edit
            </a>
            <button className="card-footer-item" onClick={onClickDelete} type="button">
              Delete
            </button>
          </footer>
        </div>
      );
    });

    return <SortableList data={_.values(components)} key={_.values(components).length} sortCallback={sortCallback} />;
  };

  return (
    <div>
      <div className="has-top-margin">
        <h1 className="title is-3" style={{ display: 'inline-block' }}>
          Focus Points
        </h1>
        <a
          className="button is-outlined is-primary"
          href={`${baseUrl}/new`}
          rel="noopener noreferrer"
          style={{ float: 'right' }}
        >
          Add Focus Point
        </a>
        {renderSaveButton()}
      </div>
      {renderFocusPointsList()}
    </div>
  );
};

export { FocusPointsContainer }
